'use client';

import React, { forwardRef } from 'react';
import Link from 'next/link';
import Container from '@/components/ui/container';
import ThemeToggle from '@/components/ui/ThemeToggle';
import { trackEvent } from '@/lib/analytics';

interface SocialLink {
	service: string;
	url: string;
}

interface NavigationItem {
	id: string;
	title: string;
	url?: string | null;
	page?: { permalink?: string | null };
	children?: NavigationItem[];
}

interface FooterProps {
	navigation: { items: NavigationItem[] };
	globals: {
		logo?: string | null;
		logo_dark_mode?: string | null;
		description?: string | null;
		social_links?: SocialLink[];
	};
}

const Footer = forwardRef<HTMLElement, FooterProps>(({ navigation }, ref) => {
	const currentYear = new Date().getFullYear();

	const getLink = (item: any) => {
		if (item.type === 'page' && item.page) {
			if (item.page.permalink === 'home') return '/';

			// Check if permalink already starts with a slash to avoid double slashes
			return item.page.permalink.startsWith('/') ? item.page.permalink : `/${item.page.permalink}`;
		}

		if (item.url) {
			return item.url.startsWith('/') ||
				item.url.startsWith('http') ||
				item.url.startsWith('mailto:') ||
				item.url.startsWith('tel:')
				? item.url
				: `/${item.url}`;
		}

		return '#';
	};

	return (
		<footer ref={ref} className="bg-blue text-white pt-16 pb-8 border-t border-white/5">
			<Container>
				{/* Dynamic Columns Grid */}
				{/* Dynamic Columns Grid + Static Columns */}
				<div
					className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${(navigation?.items?.length || 2) + 2} gap-12 mb-12`}
				>
					{/* Dynamic Navigation Columns */}
					{navigation?.items?.map((column) => (
						<div key={column.id}>
							<h3 className="text-lg font-bold mb-6 text-white font-heading">{column.title}</h3>
							<ul className="space-y-3">
								{column.children?.map((child: any) => (
									<li key={child.id}>
										<Link
											href={getLink(child)}
											className="text-blue-light/80 hover:text-white transition-colors text-base flex items-center group"
										>
											<span className="w-0 overflow-hidden group-hover:w-2 transition-all duration-300 text-orange mr-0 group-hover:mr-2">
												›
											</span>
											{child.title}
										</Link>
									</li>
								))}
								{/* Fallback if column has no children but links directly */}
								{(!column.children || column.children.length === 0) && (column.url || column.page) && (
									<li>
										<Link
											href={getLink(column)}
											className="text-blue-light/80 hover:text-white transition-colors text-base"
										>
											{column.title} Link
										</Link>
									</li>
								)}
							</ul>
						</div>
					))}

					{/* Static Column: Contact */}
					<div>
						<h3 className="text-lg font-bold mb-6 text-white font-heading">Contact</h3>
						<ul className="space-y-4">
							<li className="flex items-start space-x-3 text-base text-blue-light/80">
								<span className="text-orange mt-1">
									<img src="/images/icon-address.png" alt="" />
								</span>
								<span>
									Weg en Bos 15E, <br />
									2661 DG Bergschenhoek
								</span>
							</li>
							<li className="flex items-center space-x-3 text-base text-blue-light/80">
								<span className="text-orange">
									<img src="/images/icon-phone.png" alt="" />
								</span>
								<a href="tel:0103075610" className="hover:text-white transition-colors" onClick={() => trackEvent('phone_click', { location: 'footer' })}>
									010 - 307 56 10
								</a>
							</li>
							<li className="flex items-center space-x-3 text-base text-blue-light/80">
								<span className="text-orange">✉️</span>
								<a href="mailto:info@breuerklimaattechniek.nl" className="hover:text-white transition-colors" onClick={() => trackEvent('email_click', { location: 'footer' })}>
									info@breuerklimaattechniek.nl
								</a>
							</li>
						</ul>
					</div>

					{/* Static Column: Opening Hours */}
					<div>
						<h3 className="text-lg font-bold mb-6 text-white font-heading">Openingstijden</h3>
						<ul className="space-y-2 text-base text-blue-light/80">
							<li className="flex justify-between border-b border-white/10 pb-1">
								<span>Maandag - Vrijdag:</span>
								<span className="text-white">09:00 - 17:00</span>
							</li>
							<li className="flex justify-between border-b border-white/10 pb-1">
								<span>Zaterdag:</span>
								<span className="text-white">Gesloten</span>
							</li>
							<li className="flex justify-between border-b border-white/10 pb-1">
								<span>Zondag:</span>
								<span className="text-white">Gesloten</span>
							</li>
							<li className="flex justify-between">
								<span>Feestdagen:</span>
								<span className="text-white">Gesloten</span>
							</li>
						</ul>
					</div>
				</div>

				{/* Bottom Bar */}
				<div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-blue-light/60">
					<p>© {currentYear} Breuer Klimaattechniek. Alle rechten voorbehouden.</p>
					<div className="flex space-x-6">
						<Link href="/privacybeleid" className="hover:text-white transition-colors">
							Privacybeleid
						</Link>
						<Link href="/algemene-voorwaarden" className="hover:text-white transition-colors">
							Algemene Voorwaarden
						</Link>
					</div>
				</div>
			</Container>
		</footer>
	);
});

Footer.displayName = 'Footer';
export default Footer;
